import { useState, MouseEvent } from 'react';
import Box from '@mui/material/Box';
import { IconButton } from '../../components/IconButton';
import { Menu } from '../../components/Menu';
import { MenuItem } from '../../components/Menu';

import { buildLocaleId, Locale } from './core/locale';
import { NAVBAR_ICON_SIZE } from './navbar-icon-size';

export interface LanguageMenuProps {
  locales: Locale[];
  onChangeLanguage: (locale: Locale) => void;
}

// Rótulos amigáveis por idioma; a UI mostra o nome legível em vez do id cru.
const LOCALE_LABELS: Record<string, string> = {
  pt_BR: 'Português (BR)',
  es_MX: 'Español (MX)',
  en_US: 'English (US)',
};

// Fallback ao próprio id para locales fora da lista conhecida (sem hardcodar
// tradução para idiomas ainda não previstos).
function buildLocaleLabel(locale: Locale): string {
  const id = buildLocaleId(locale);
  return LOCALE_LABELS[id] ?? id;
}

/**
 * Botão de idioma sempre visível (evita deslocamento de layout quando os locales
 * ainda não chegaram ou o endpoint falha); a lista de opções fica vazia nesse caso,
 * sem erro.
 */
export function LanguageMenu({ locales, onChangeLanguage }: LanguageMenuProps): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);

  function handleOpen(event: MouseEvent<HTMLElement>): void {
    setAnchorEl(event.currentTarget);
  }

  function handleClose(): void {
    setAnchorEl(null);
  }

  function handleSelect(locale: Locale): void {
    onChangeLanguage(locale);
    handleClose();
  }

  return (
    <>
      <IconButton
        aria-label="Idioma"
        aria-controls={isOpen ? 'language-menu' : undefined}
        aria-haspopup="true"
        onClick={handleOpen}
        color="inherit"
      >
        {/* Glifo da fonte do legado (centernovo), o mesmo `centericon-idioma` da navbar
            Angular. O mapeamento vale dentro do slot da navbar; fora dele (Storybook,
            que não carrega o SCSS legado) o elemento fica vazio. */}
        <Box component="i" className="centericon-idioma" sx={{ fontSize: NAVBAR_ICON_SIZE }} />
      </IconButton>
      <Menu id="language-menu" anchorEl={anchorEl} open={isOpen} onClose={handleClose}>
        {locales.map((locale) => (
          <MenuItem key={buildLocaleId(locale)} onClick={() => handleSelect(locale)}>
            {buildLocaleLabel(locale)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
